import {
  ScaffoldDropZone,
  type IScaffoldZoneColorResolverParams,
  type IScaffoldZoneColorResolverResult,
} from "../../scaffold";
import {
  BOTTOM_HEADER_ZONES,
  CENTRE_HEADER_ZONES,
  EMPTY_HEADER_COL_ID,
  HEADER_ROW_ID,
  LEFT_HEADER_ZONES,
  RIGHT_HEADER_ZONES,
} from "./interface";

/**
 * All header drop zones grouped into a single set for use with the empty column header.
 * When there are no existing columns the entire cell acts as one drop target.
 */
const ALL_HEADER_ZONES: ScaffoldDropZone[] = [
  ...LEFT_HEADER_ZONES,
  ...CENTRE_HEADER_ZONES,
  ...RIGHT_HEADER_ZONES,
  ...BOTTOM_HEADER_ZONES,
];

/**
 * Drop-zone colours used for each header placement action.
 */
const HEADER_ZONE_GROUP_COLORS = {
  left: "var(--bs-primary)",
  centre: "var(--bs-primary)",
  right: "var(--bs-primary)",
  bottom: "var(--bs-primary)",
};

/**
 * Builds a colour resolution result for one grouped header action.
 *
 * @param color - Highlight colour for the grouped action.
 * @param zone - Active drop zone.
 * @param groupedZones - Zones belonging to the same action.
 * @returns Zone highlight result with sibling zones included.
 */
const buildGroupedHeaderZoneResult = (
  color: string,
  zone: ScaffoldDropZone,
  groupedZones: ScaffoldDropZone[],
): IScaffoldZoneColorResolverResult => {
  return {
    color,
    additionalHighlightedZones: groupedZones.filter((groupedZone) => groupedZone !== zone),
  };
};

/**
 * Resolves scaffold zone highlighting for ArsListing drag-and-drop.
 *
 * Header cells highlight grouped left/centre/right/bottom zones because each group maps to one
 * placement action. Non-header rows are blocked by returning a null colour.
 * Bottom zones map to the add-variables-to-column action, except when the column is the empty
 * column header (no variables yet), where all header zones are grouped together so the entire
 * cell acts as a single drop target.
 *
 * @param params - Zone resolver parameters from the scaffold.
 * @param params.rowId - Id of the row containing the zone.
 * @param params.colId - Id of the column containing the zone.
 * @param params.zone - Active drop zone being evaluated.
 * @returns Zone highlight configuration for the current target.
 */
export const arsListingZoneColorResolver = ({
  rowId,
  colId,
  zone,
}: IScaffoldZoneColorResolverParams): IScaffoldZoneColorResolverResult => {
  if (rowId !== HEADER_ROW_ID) {
    return {
      color: null,
      additionalHighlightedZones: [],
    };
  }

  if (colId === EMPTY_HEADER_COL_ID) {
    return buildGroupedHeaderZoneResult(HEADER_ZONE_GROUP_COLORS.left, zone, ALL_HEADER_ZONES);
  }

  if (LEFT_HEADER_ZONES.includes(zone)) {
    return buildGroupedHeaderZoneResult(HEADER_ZONE_GROUP_COLORS.left, zone, LEFT_HEADER_ZONES);
  }

  if (RIGHT_HEADER_ZONES.includes(zone)) {
    return buildGroupedHeaderZoneResult(HEADER_ZONE_GROUP_COLORS.right, zone, RIGHT_HEADER_ZONES);
  }

  if (BOTTOM_HEADER_ZONES.includes(zone)) {
    return buildGroupedHeaderZoneResult(HEADER_ZONE_GROUP_COLORS.bottom, zone, BOTTOM_HEADER_ZONES);
  }

  return buildGroupedHeaderZoneResult(HEADER_ZONE_GROUP_COLORS.centre, zone, CENTRE_HEADER_ZONES);
};
